import React from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  styled,
  Grid,
  Card,
  CardContent,
  Box,
  useTheme,
  Link,
} from "@mui/material";
import { Slide } from "@mui/material";
import { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useFirstLoginMutation } from "../../features/users/usersApiSlice";
import { setDialog } from "../../features/dialog/dialogSlice";
import { startTour } from "../../features/tour/tourSlice";
import useAuth from "../../hooks/useAuth";

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const WelcomeMessage = styled(Typography)(({ theme }) => ({
  fontWeight: "bold",
  fontSize: theme.typography.h4.fontSize,
  textAlign: "center",
  marginBottom: theme.spacing(1),
}));

const featureCards = [
  {
    icon: "fa-solid:users",
    title: "Mitglieder verwalten",
    description: "Mitglieder anlegen, bearbeiten und organisieren.",
    color: "#1976d2",
  },
  {
    icon: "fa-solid:torii-gate",
    title: "Standorte einrichten",
    description: "Standorte erstellen und Mitglieder zuweisen.",
    color: "#7b1fa2",
  },
  {
    icon: "fa-solid:file-invoice-dollar",
    title: "Verträge & Banking",
    description: "Vertragsdaten pflegen und SEPA-Lastschriften erstellen.",
    color: "#2e7d32",
  },
  {
    icon: "fa-solid:star",
    title: "Veranstaltungen planen",
    description: "Events erstellen und Teilnehmer verwalten.",
    color: "#ed6c02",
  },
  {
    icon: "fa-solid:award",
    title: "Prüfungen & Turniere",
    description: "Gürtelprüfungen und Wettkämpfe dokumentieren.",
    color: "#d32f2f",
  },
  {
    icon: "mdi:database-import",
    title: "Daten importieren",
    description: "Bestehende Daten aus CSV oder JSON importieren.",
    color: "#0288d1",
  },
];

const WelcomeDialog = ({ handleClose }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [showAnimation, setShowAnimation] = useState(false);
  const [firstLogin] = useFirstLoginMutation();

  useEffect(() => {
    setShowAnimation(true);
  }, []);

  const handleDontShowAgain = async () => {
    try {
      let result = await firstLogin().unwrap();
      if (result && result?.status === "SUCCESS") {
        handleClose();
      } else {
        window.alert(
          "Fehler bei der Kommunikation mit dem Server bitte versuchen Sie es erneut.",
        );
      }
    } catch (error) {
      console.log("error :>> ", error);
      window.alert(
        "Fehler bei der Kommunikation mit dem Server bitte versuchen Sie es erneut.",
      );
    }
  };

  const openCreate = () => {
    dispatch(setDialog({ title: "Neuen Standort erstellen", type: "CREATE" }));
  };

  const openImportWizard = () => {
    dispatch(
      setDialog({
        title: "Daten importieren",
        type: "DATA_IMPORT_WIZARD",
        content: "data-import",
      }),
    );
  };

  const handleStartTour = () => {
    handleClose();
    dispatch(startTour());
  };

  const navigateToPath = (path) => {
    handleClose();
    navigate(path);
  };

  return (
    <Dialog
      TransitionComponent={Transition}
      open={true}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogActions
        style={{
          borderBottomStyle: "solid",
          borderWidth: "2px",
          borderColor: "lightgrey",
          paddingTop: 0,
          justifyContent: "flex-end",
        }}
      >
        <Button onClick={handleClose} color="inherit">
          <Icon width="40" icon="icon-park-solid:close-one" />
        </Button>
      </DialogActions>
      <DialogContent>
        <Slide direction="down" in={showAnimation} timeout={600}>
          <div>
            <WelcomeMessage variant="h4">
              Willkommen zu Martial Artministration!
            </WelcomeMessage>
          </div>
        </Slide>
        <Typography
          variant="subtitle1"
          color="text.secondary"
          textAlign="center"
          sx={{ mb: 3 }}
        >
          Kampfkunst trifft Verwaltungssoftware.
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {featureCards.map((card) => (
            <Grid item xs={12} sm={6} md={4} key={card.title}>
              <Card
                variant="outlined"
                sx={{
                  height: "100%",
                  transition: "box-shadow 0.2s",
                  "&:hover": { boxShadow: theme.shadows[4] },
                }}
              >
                <CardContent sx={{ textAlign: "center", py: 2.5 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      bgcolor: `${card.color}14`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 1.5,
                    }}
                  >
                    <Icon
                      icon={card.icon}
                      width={24}
                      style={{ color: card.color }}
                    />
                  </Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    gutterBottom
                  >
                    {card.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {card.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            justifyContent: "center",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant="body1"
            textAlign="center"
            sx={{ maxWidth: 720, mb: 0.5 }}
          >
            Starten Sie hier: Die Tour zeigt Ihnen Schritt für Schritt alle
            wichtigen Bereiche. Alternativ können Sie direkt Daten importieren
            oder Ihren ersten Standort anlegen.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleStartTour}
            startIcon={<Icon icon="mdi:compass-outline" />}
            sx={{
              boxShadow: "0 10px 28px rgba(25, 118, 210, 0.28)",
              animation: "tourPulse 2.6s ease-in-out infinite",
              "@keyframes tourPulse": {
                "0%": {
                  transform: "translateY(0)",
                  boxShadow: "0 10px 28px rgba(25, 118, 210, 0.24)",
                },
                "50%": {
                  transform: "translateY(-1px)",
                  boxShadow: "0 14px 36px rgba(25, 118, 210, 0.38)",
                },
                "100%": {
                  transform: "translateY(0)",
                  boxShadow: "0 10px 28px rgba(25, 118, 210, 0.24)",
                },
              },
            }}
          >
            Geführte Tour starten
          </Button>
          {isAdmin && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                justifyContent: "center",
              }}
            >
              <Button
                variant="contained"
                color="secondary"
                onClick={openImportWizard}
                startIcon={<Icon icon="mdi:database-import" />}
              >
                Daten jetzt importieren
              </Button>
              <Button
                variant="outlined"
                onClick={openCreate}
                startIcon={<Icon icon="mdi:pencil" />}
              >
                Standort erstellen
              </Button>
            </Box>
          )}
        </Box>
        {isAdmin && (
          <>
            <Alert
              severity="info"
              icon={<Icon icon="mdi:lightbulb-on-outline" width={20} />}
              sx={{ mt: 2.5 }}
            >
              Für einen professionellen Start: Hinterlegen Sie Ihr Logo unter{" "}
              <Link
                component="button"
                type="button"
                underline="hover"
                onClick={() =>
                  navigateToPath("/dashboard/settings/tenant/logo")
                }
              >
                System &gt; Logo
              </Link>{" "}
              und richten Sie Ihre SMTP-Zugangsdaten unter{" "}
              <Link
                component="button"
                type="button"
                underline="hover"
                onClick={() =>
                  navigateToPath("/dashboard/settings/mail/config")
                }
              >
                Einstellungen &gt; E-Mail Konfiguration
              </Link>{" "}
              ein. Die Mail-Einstellungen sind entscheidend, damit das System
              automatisierte E-Mails zuverlässig versenden kann.
            </Alert>
            <Box sx={{ mt: 0.5 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                Tipp: Starten Sie mit der Tour und öffnen Sie anschließend Logo
                und E-Mail Konfiguration in Ruhe nacheinander.
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="outlined" color="primary">
          SCHLIESSEN
        </Button>
        <Button onClick={handleDontShowAgain} variant="contained" color="error">
          NICHT WIEDER ANZEIGEN
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WelcomeDialog;
